import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "../../component/Common/Button";
import Input from "../../component/Common/Input";
import Loader from "../../component/Common/Loader";
import { routes } from "../../constants";
import { useForm } from "../../hooks/useForm";
import { createContact, updateContact } from "../../store/slice/contactSlice";
import { getServiceList } from "../../store/slice/serviceSlice";
import CreatableSelect from "react-select/creatable";
import TextArea from "../../component/Common/TextArea";
import { imageProps } from "../../props";
import { fileUpload } from "../../helpers/fileUpload";
import { getProjectList } from "../../store/slice/projectSlice";
import { addIcon, removeIcon } from "../../icons";

const CreateContactPage = ({ contactDetails }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { id, page_number } = params;

  const [uploading, setUploading] = useState(false);
  const [duplicateProjectIdError, setDuplicateProjectIdError] = useState("");

  const serviceList = useSelector((state) => state?.service?.serviceList?.data);
  const projectList = useSelector((state) => state?.project?.projects);
  const loader = useSelector((state) => state?.contact?.loader);
  const serviceLoader = useSelector((state) => state?.service?.loader);
  const projectLoader = useSelector((state) => state?.project?.loader);

  useEffect(() => {
    dispatch(getServiceList({ start: 0, limit: 10000 }));
    dispatch(getProjectList({ admin: "true" }));
  }, []);

  let serviceDropdown = serviceList?.map((item) => ({
    id: item?.id,
    label: item?.service_name,
    value: item?.id,
  }));

  let projectOptions = projectList?.map((item) => ({
    value: item?.name,
    label: item?.name,
    id: item?.id,
  }));

  const parsedContactProperties = useMemo(() => {
    return contactDetails?.contact_properties
      ? JSON.parse(contactDetails?.contact_properties)
      : [];
  }, [contactDetails]);

  const parsedTowerLists = useMemo(() => {
    return projectList?.map((item) => ({
      id: item?.id,
      tower_names: item?.tower_names && JSON.parse(item?.tower_names),
    }));
  }, [projectList]);

  const [contactProperties, setContactProperties] = useState([]);

  function updateContactProperty(parsedTowerList, parsedContactProperties) {
    return parsedContactProperties.map((contactProperty) => {
      const parsedTower = parsedTowerList.find(
        (tower) => tower.id === contactProperty.project_id
      );

      if (!parsedTower) {
        return contactProperty;
      }

      let towerList = contactProperty?.towerList || [];
      let towerProperties = contactProperty?.tower_properties || [];

      let towerMap = new Map();
      towerList.forEach((tower) => {
        towerMap.set(tower.name, tower.isChecked);
      });

      let updatedTowerList = parsedTower.tower_names.map((tower) => ({
        name: tower.name,
        property: tower.property,
        isChecked: towerMap.has(tower.name) ? towerMap.get(tower.name) : false,
      }));

      let updatedTowerProperties = updatedTowerList?.map((tower) => {
        let towerProperty = towerProperties.find(
          (towerProp) => towerProp.tower === tower.name
        );
        return towerProperty
          ? towerProperty
          : { tower: tower.name, properties: [] };
      });

      return {
        ...contactProperty,
        towerList: updatedTowerList,
        tower_properties: updatedTowerProperties,
      };
    });
  }

  useEffect(() => {
    const updateProperty = updateContactProperty(
      parsedTowerLists,
      parsedContactProperties
    );
    setContactProperties(
      updateProperty.length > 0
        ? updateProperty.map((contact) => ({
            project_id: {
              value: contact.project_id,
              label:
                contact.project_name === ""
                  ? "Select Project..."
                  : contact.project_name,
              id: contact.project_id,
            },
            project_name: contact.project_name,
            towerList: contact.towerList || [],
            towerProperties:
              contact.tower_properties?.map((tower) => ({
                tower: tower?.tower,
                properties: tower?.properties || [],
              })) || [],
            certificate: contact.certificate || "",
            certificate_preview: contact.certificate
              ? `${process.env.REACT_APP_FILE_BASE_URL}/${contact.certificate}`
              : "",
          }))
        : [
            {
              project_id: null,
              project_name: "",
              towerList: [],
              towerProperties: [{ tower: "", properties: [] }],
              certificate: "",
              certificate_preview: "",
            },
          ]
    );
  }, [parsedContactProperties, parsedTowerLists]);

  const checkDuplicateProjectId = (projectId) => {
    const existingIds = contactProperties?.map((item) => item.project_id?.id);
    return existingIds?.includes(projectId);
  };

  const initialFValues = {
    company_name: contactDetails?.company_name || "",
    contact_name: contactDetails?.contact_name || "",
    phone_number: contactDetails?.phone_number || "",
    service_id: contactDetails?.service_id
      ? {
          value: contactDetails?.service_id,
          id: contactDetails?.service_id,
          label: contactDetails?.service_name,
        }
      : null,
    contact_address: contactDetails?.contact_address || "",
  };

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ("company_name" in fieldValues) {
      temp.company_name = fieldValues.company_name
        ? ""
        : "Company Name is required !";
    }
    if ("contact_name" in fieldValues) {
      temp.contact_name = fieldValues.contact_name ? "" : "Name is required !";
    }
    if ("phone_number" in fieldValues) {
      temp.phone_number = !fieldValues.phone_number
        ? "Phone number is required !"
        : fieldValues.phone_number.length === 10
        ? ""
        : "Phone number is not valid !";
    }
    if ("service_id" in fieldValues) {
      temp.service_id = fieldValues?.service_id ? "" : "Service is required !";
    }
    if ("contact_address" in fieldValues) {
      temp.contact_address = fieldValues?.contact_address
        ? ""
        : "Address is required";
    }

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    true,
    validate
  );

  const handleCertificateChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    let tempErrors = {};
    if (file.size > 5000000) {
      tempErrors.certificate = "File size should be less than 5Mb !";
    } else if (!file.name.match(/.(jpg|jpeg|png|gif|pdf)$/i)) {
      tempErrors.certificate = "Please select a valid File !";
    } else {
      tempErrors.certificate = "";
    }

    if (tempErrors.certificate) {
      const newProperties = [...contactProperties];
      newProperties[index].certificate = "";
      newProperties[index].certificate_preview = "";
      newProperties[index].certificate_blob = null;
      setContactProperties(newProperties);
      setErrors({ ...errors, [`certificate_${index}`]: tempErrors.certificate });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const newProperties = [...contactProperties];
      newProperties[index].certificate = file;
      newProperties[index].certificate_preview = file.name.match(/\.pdf$/i)
        ? "" // Don't set preview for PDFs until uploaded
        : reader.result;
      newProperties[index].certificate_blob = file.name.match(/\.pdf$/i)
        ? URL.createObjectURL(file)
        : null;
      setContactProperties(newProperties);
      setErrors({ ...errors, [`certificate_${index}`]: "" });
    };
    reader.readAsDataURL(file);
  };

  const certificateRemoveHandler = (index) => {
    const newProperties = [...contactProperties];
    newProperties[index].certificate = "";
    newProperties[index].certificate_preview = "";
    newProperties[index].certificate_blob = null;
    setContactProperties(newProperties);
    setErrors({ ...errors, [`certificate_${index}`]: "" });
  };

  const onProjectChange = (type, e, i, l) => {
    if (type === "project_id") {
      if (checkDuplicateProjectId(e?.id)) {
        setDuplicateProjectIdError("Project is already assigned to this contact");
        return;
      }
      setDuplicateProjectIdError("");

      let projectList = [...contactProperties];
      projectList[i].project_id = e;
      projectList[i].project_name = e?.label;
      projectList[i].certificate = "";
      projectList[i].certificate_preview = "";
      projectList[i].certificate_blob = null;

      const selectedId = e ? e?.id : null;
      const selectedProject = parsedTowerLists?.find(
        (item) => item?.id === selectedId
      );

      const finalTowerNames =
        selectedProject?.tower_names?.map((tower) => ({
          name: tower?.name,
          property: tower?.property,
          id: selectedId,
          isChecked: false,
        })) ?? [];

      projectList[i].towerList = finalTowerNames;
      projectList[i].towerProperties = finalTowerNames?.map((tower) => ({
        tower: tower?.name,
        properties: [],
      }));
      setContactProperties(projectList);
    } else if (type === "properties") {
      let projectList = [...contactProperties];
      if (
        projectList[i]?.towerProperties &&
        projectList[i]?.towerProperties[l]
      ) {
        projectList[i].towerProperties[l].properties =
          e?.map((property) => ({
            label: property?.value,
            value: property?.value,
          })) || [];
      }
      setContactProperties(projectList);
    }
  };

  const onCheckboxChange = (e, objIndex, itemIndex) => {
    let check = e?.target?.checked;
    let list = [...contactProperties];
    if (
      list[objIndex]?.towerList &&
      list[objIndex]?.towerList[itemIndex]
    ) {
      list[objIndex].towerList[itemIndex].isChecked = check;
      const towerName = list[objIndex]?.towerList[itemIndex]?.name;
      const towerPropertiesIndex = list[objIndex]?.towerProperties?.findIndex(
        (towerProp) => towerProp?.tower === towerName
      );
      if (towerPropertiesIndex !== -1) {
        list[objIndex].towerProperties[towerPropertiesIndex].isChecked = check;
        if (!check) {
          list[objIndex].towerProperties[towerPropertiesIndex].properties = [];
        }
      }
    }
    setContactProperties(list);
  };

  const contactsPropertyAddClick = () => {
    setContactProperties((prevVal) => [
      ...prevVal,
      {
        project_id: null,
        project_name: "",
        towerList: [],
        towerProperties: [{ tower: "", properties: [] }],
        certificate: "",
        certificate_preview: "",
        certificate_blob: null,
      },
    ]);
  };

  const contactsPropertyRemoveClick = (i) => {
    setDuplicateProjectIdError("");
    const list = [...contactProperties];
    list.splice(i, 1);
    setContactProperties(list);
  };

  const getPropertiesOption = (tower) => {
    return tower?.property?.map((propertyItem) => ({
      value: propertyItem,
      label: propertyItem,
    }));
  };

  const extractValuesToSendInApi = async () => {
    const finalArr = [];
    for (const item of contactProperties) {
      let certificateUrl = item.certificate;
      if (item.certificate && typeof item.certificate !== "string") {
        const formData = new FormData();
        formData.append("file", item.certificate);
        certificateUrl = await fileUpload(formData);
      }

      finalArr.push({
        project_id: item?.project_id?.id || null,
        project_name: item?.project_name,
        towerList: item?.towerList,
        tower_properties: item?.towerProperties,
        certificate: certificateUrl,
      });
    }
    return finalArr;
  };

  const validateCertificates = () => {
    let isValid = true;
    const newErrors = {};
    
    contactProperties.forEach((item, index) => {
      if (item.project_id && !item.certificate) {
        newErrors[`certificate_${index}`] = "Certificate is required for selected project!";
        isValid = false;
      }
    });

    setErrors({ ...errors, ...newErrors });
    return isValid;
  };

  const createContactHandler = async (e) => {
    e.preventDefault();
    setUploading(true);

    if (!validate() || !validateCertificates()) {
      setUploading(false);
      return;
    }

    const contact_properties = await extractValuesToSendInApi();

    const data = {
      contact_name: values.contact_name.trim(),
      phone_number: values.phone_number.trim(),
      service_id: values.service_id.id,
      contact_address: values.contact_address,
      company_name: values.company_name,
      contact_properties,
    };

    if (id) {
      dispatch(updateContact({ data, id, navigate, page_number }));
    } else {
      dispatch(createContact({ data, navigate }));
    }
    setUploading(false);
  };

  return (
    <>
      {(uploading || loader || serviceLoader || projectLoader) && <Loader />}
      <div className="comman_btn_container center_back_btn">
        <Link
          to={`${routes.contacts}/page/${page_number ? page_number : 1}`}
          className="comman_btn back_btn"
        >
          Back
        </Link>
      </div>
      <div className="project_edit_main_content">
        <div className="create_from_row">
          <div className="create_from_input_content">
            <label className="create_from_label">Service Name</label>
            <CreatableSelect
              value={values.service_id}
              className="basic-multi-select"
              classNamePrefix="select"
              name="service_id"
              options={serviceDropdown}
              onChange={(e) => handleInputChange(e, "service_id")}
            />
            <span className="err_text">{errors?.service_id || ""}</span>
          </div>
          <Input
            className="create_from_input_content"
            labelClassName="create_from_label"
            inputClassName="create_from_input"
            errorClassName="err_text"
            type="text"
            placeholder="Company Name"
            label="Company Name"
            id="company_name"
            name="company_name"
            value={values.company_name}
            onChange={handleInputChange}
            error={errors?.company_name || ""}
          />
          <Input
            className="create_from_input_content input_css"
            labelClassName="create_from_label"
            inputClassName="create_from_input"
            errorClassName="err_text"
            type="text"
            placeholder="Enter Name"
            label="Dealer Name"
            id="contact_name"
            name="contact_name"
            value={values.contact_name}
            onChange={handleInputChange}
            error={errors?.contact_name || ""}
          />
          <Input
            className="create_from_input_content input_css"
            labelClassName="create_from_label"
            inputClassName="create_from_input"
            errorClassName="err_text"
            type="tel"
            placeholder="Enter Phone Number"
            label="Phone Number"
            id="phone_number"
            name="phone_number"
            value={values.phone_number}
            onChange={handleInputChange}
            error={errors?.phone_number || ""}
            maxLength={10}
          />
          <TextArea
            className="create_from_input_content"
            labelClassName="create_from_label"
            inputClassName="create_from_input"
            errorClassName="err_text"
            type="text"
            placeholder="Enter Address"
            label="Address"
            id="contact_address"
            name="contact_address"
            rows={5}
            value={values.contact_address}
            onChange={handleInputChange}
            error={errors?.contact_address || ""}
          />
          <div className="contact_property_main_wrapper">
            <h3>Select Properties</h3>
            {contactProperties?.map((items, i) => (
              <div className="contact-properties-container" key={i}>
                <div className="create_from_input_content">
                  <label className="create_from_label">Select Project</label>
                  <CreatableSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select Project..."
                    id="project_id"
                    name="project_id"
                    options={projectOptions}
                    onChange={(e) => onProjectChange("project_id", e, i)}
                    menuPortalTarget={document?.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    value={items?.project_id}
                  />
                </div>
                <Input
                  className="create_from_input_content"
                  labelClassName="create_from_label"
                  errorClassName="err_text"
                  type="file"
                  id={`certificate_${i}`}
                  name={`certificate_${i}`}
                  label="Project Certificate"
                  accept=".jpg, .jpeg, .png, .pdf"
                  onChange={(e) => handleCertificateChange(e, i)}
                  error={errors[`certificate_${i}`] || ""}
                  onClick={(e) => (e.target.value = null)}
                  imageProps={imageProps(
                    items.certificate,
                    items.certificate_preview,
                    () => certificateRemoveHandler(i),
                    `certificate_${i}`
                  )}
                />
                {items.certificate_blob && (
                  <p>
                    <a
                      href={items.certificate_blob}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Certificate
                    </a>
                  </p>
                )}
                {items.certificate_preview && items.certificate && typeof items.certificate === "string" && (
                  <p>
                    <a
                      href={`${process.env.REACT_APP_FILE_BASE_URL}/${items.certificate}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Certificate
                    </a>
                  </p>
                )}
                <div className="create_from_checkbox_content">
                  {items?.towerList?.length > 0 && (
                    <div>
                      <h3>TowerList</h3>
                      <div className="create-contact-check-container">
                        {items?.towerList?.map((tower, j) => (
                          <div className="create-contact-check-wrapper" key={j}>
                            <input
                              type="checkbox"
                              name={`tower_${i}_${j}`}
                              id={`tower_${i}_${j}`}
                              onChange={(e) => onCheckboxChange(e, i, j)}
                              checked={tower?.isChecked}
                              className="contact_checkbox"
                            />
                            <label htmlFor={`tower_${i}_${j}`}>
                              {tower?.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="main_temp_class">
                  {items?.towerList?.length > 0 &&
                    items?.towerList?.map(
                      (tower, l) =>
                        tower?.isChecked && (
                          <div className="property_tower_wrapper" key={l}>
                            <div className="property_tower_label">
                              <label className="property_label">
                                {tower?.name}
                              </label>
                            </div>
                            <div className="property_tower_select">
                              <label className="create_from_label">
                                Properties
                              </label>
                              <CreatableSelect
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder="Select Properties..."
                                name={`properties_${i}_${l}`}
                                id={`properties_${i}_${l}`}
                                isMulti
                                options={getPropertiesOption(tower)}
                                onChange={(e) =>
                                  onProjectChange("properties", e, i, l)
                                }
                                menuPortalTarget={document?.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                    height: 20,
                                    minHeight: 20,
                                  }),
                                }}
                                value={items.towerProperties[l]?.properties?.map(
                                  (property) => ({
                                    value: property?.value,
                                    label: property?.label,
                                  })
                                )}
                              />
                            </div>
                          </div>
                        )
                    )}
                </div>
                <div className="contact_multi_select_wrapper">
                  <div className="project_edit_feature_add_remove_btn_row">
                    {contactProperties?.length - 1 === i && (
                      <Button
                        buttonClassName="project_edit_feature_add_remove_btn project_edit_feature_add_btn"
                        onClick={() => contactsPropertyAddClick(i)}
                        other={addIcon}
                      />
                    )}
                    {contactProperties?.length !== 1 && (
                      <Button
                        buttonClassName="project_edit_feature_add_remove_btn project_edit_feature_add_btn"
                        onClick={() => contactsPropertyRemoveClick(i)}
                        other={removeIcon}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {duplicateProjectIdError && (
            <span className="error_text">{duplicateProjectIdError}</span>
          )}
          <Button
            className="create_from_bottom_btn col_span_2"
            buttonClassName="comman_btn"
            onClick={createContactHandler}
            text={id ? "Edit Contact" : "Create Contact"}
          />
        </div>
      </div>
    </>
  );
};

export default CreateContactPage;